import React, { useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'store/hooks';
import { translateBlockValue } from 'i18n';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { Box, useTheme } from '@mui/material';
import { BudgetPhases } from 'business/constants';
import { InscriptionWithMetadata } from 'routes/BudgetModule/config/types';
import {
    getStatusesValuesForPhase,
    getStatusColor,
    isStatusVoted,
} from 'routes/BudgetModule/helpers/statusHelpers';
import { getMutationInformationForPhase } from 'routes/BudgetModule/shared/phases/helpers';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import SimpleModal from 'ui/Modals/SimpleModal';
import { Trans, useTranslation } from 'react-i18next';
import { useGroupUpdateInscriptionStatus } from '../hooks/inscriptions';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { PHASE_ID, PHASE_STATUS } from 'routes/BudgetModule/config/constants';

const StyStatuses = styled(Box, {
    name: 'StyStatuses',
})(({ theme }) => ({
    display: 'flex',
    [theme.breakpoints.up('md')]: {
        width: '75%',
    },
    alignItems: 'center',
    justifyContent: 'left',
    overflow: 'auto',
}));

const StyButton = styled(Button, {
    name: 'StyButton',
})({
    height: '52px',
    minHeight: '52px',
    marginRight: '2rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

type Props = {
    selectedInscriptions: InscriptionWithMetadata[];
    inscriptions: InscriptionWithMetadata[];
    phase: BudgetPhases;
    queryCacheKey: InscriptionQueryKey;
    gridApi: FlowAnyObject;
};

export default function StatusOptions({
    selectedInscriptions,
    inscriptions,
    phase,
    queryCacheKey,
    gridApi,
}: Props): React.ReactElement {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const theme = useTheme();
    const { t } = useTranslation();
    const statusesSettings = useContext(StatusSettingsContext);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');
    const { endpoint, phaseInscriptionIdColumn } =
        getMutationInformationForPhase(phase, true);

    const updateStatusMutation = useGroupUpdateInscriptionStatus(
        dispatch,
        endpoint,
        queryClient,
        phaseInscriptionIdColumn,
        gridApi,
        queryCacheKey,
    );

    const statuses = phase
        ? getStatusesValuesForPhase(phase, statusesSettings).filter(
              (status) => !isStatusVoted(status, statusesSettings),
          )
        : [];

    const hasSelectedInscriptionAlreadyTheStatus = (
        selectedInscription: InscriptionWithMetadata,
        status: string,
    ) => {
        /* As the selectedInscriptions object is not updated when a status is modified individually,
         we need to check directly the inscriptions object to ensure the selectedInscription does not have
         the wanted status already. */

        const inscription = inscriptions.find(
            (i: InscriptionWithMetadata) =>
                i.inscription[PHASE_ID] ===
                selectedInscription.inscription[PHASE_ID],
        );
        return inscription && inscription.inscription[PHASE_STATUS] === status;
    };

    const bulkMutateInscriptions = () => {
        if (!selectedStatus) return;
        updateStatusMutation.mutate({
            ids: selectedInscriptions
                .filter(
                    (inscription) =>
                        !hasSelectedInscriptionAlreadyTheStatus(
                            inscription,
                            selectedStatus,
                        ),
                )
                .map(
                    (inscription: InscriptionWithMetadata) =>
                        inscription.inscription[PHASE_ID],
                ),
            status: selectedStatus,
        });
        if (gridApi) {
            gridApi.deselectAll();
        }
    };

    return (
        <StyStatuses>
            {statuses.map((status) => (
                <Tooltip title={translateBlockValue(status)} key={status}>
                    <StyButton
                        key={status}
                        onClick={() => {
                            setIsConfirmModalOpen(true);
                            setSelectedStatus(status);
                        }}
                        variant="outlined"
                        style={{
                            color: getStatusColor(status, statusesSettings),
                            borderColor: getStatusColor(
                                status,
                                statusesSettings,
                            ),
                        }}
                    >
                        {translateBlockValue(status)}
                    </StyButton>
                </Tooltip>
            ))}
            <SimpleModal
                isSmallHeight
                open={isConfirmModalOpen}
                onClose={() => {
                    setIsConfirmModalOpen(false);
                }}
                additionalButtons={[
                    <Button
                        onClick={bulkMutateInscriptions}
                        variant="contained"
                        sx={{ ml: '1rem' }}
                        key="confirm"
                    >
                        {t('common.confirm')}
                    </Button>,
                ]}
            >
                <Trans
                    i18nKey="budgetProposal.confirmPhaseChange"
                    count={selectedInscriptions.length}
                    tOptions={{
                        numberOfInscriptions: selectedInscriptions.length,
                        status: translateBlockValue(selectedStatus),
                    }}
                    components={{
                        bold: (
                            <strong
                                style={{ color: theme.palette.primary.main }}
                            />
                        ),
                    }}
                />
            </SimpleModal>
        </StyStatuses>
    );
}
